import React, { useEffect, useState } from 'react'
import { AutoComplete, Button, DatePicker, Dropdown, Form, Input, InputNumber, Modal, Select, Space, message } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { get, post, patch } from '../lib/request'
import { BusinessFormat, LeadItem, Unit, errMsg, LEAD_STATUS_LABELS, LeadStatus } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { hasPermission } from '../utils/permissions'

/**
 * 线索操作按钮组（SOP 主链路入口）：
 * 标记已联系 / 添加跟进 / 带看登记 / 成交录入（楼盘字典选择）/ 转无效（原因必填）/ 手动转移
 * 各操作按状态机前置态控制可用性（后端亦 409 校验，双重保险）
 *
 * thankyou_v1（批次 2）：成交录入弹窗增「成交业态」下拉（选项自动取自业态表启用项，值=code，可清空）
 * → POST /admin/leads/:id/deal payload +dealCategory（清空传 null，后端非空时校验启用业态 code，非法 400）。
 *
 * R4 权限门禁：五个动作按钮原先仅按 lead.status 显隐、无角色判断，导致 finance /
 * finance_manager / leasing_director 看得见按钮、一点必 403（R3 已上线的现网缺陷）。
 * 现补两层门禁，与后端 leads.ts 的归属守卫同构：
 *   1) 权限层：持 LEAD_EDIT（SA/A）或 LEAD_EDIT_OWN（operator）
 *   2) 归属层：仅持 LEAD_EDIT_OWN 者，还需 lead.assigneeId === 当前登录 adminId
 * 前端只做显隐，后端 403 仍是权威闸门。
 */
interface Props {
  lead: LeadItem
  canTransfer: boolean
  onDone: () => void
  // leads-mobile-fix：true 时六个动作按钮收进 ⋯ Dropdown 菜单（权限/状态过滤逻辑不变），桌面端不传保持平铺
  mobile?: boolean
}

// deal_required_20260916：成交单元 AutoComplete 选项值形态
// 选中字典项 → 数字 id；手动输入 → 字符串文本（提交时区分）
type UnitFieldValue = number | string | undefined

type ModalKind = 'followup' | 'tour' | 'deal' | 'invalid' | 'transfer' | null

const LeadStatusActions: React.FC<Props> = ({ lead, canTransfer, onDone, mobile = false }) => {
  const [kind, setKind] = useState<ModalKind>(null)
  const [submitting, setSubmitting] = useState(false)
  const [units, setUnits] = useState<Unit[]>([])
  // thankyou_v1（批次 2）：成交业态下拉选项（业态表 published 项，值=code）
  const [businessFormats, setBusinessFormats] = useState<BusinessFormat[]>([])
  const [form] = Form.useForm()
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    if (kind === 'deal') {
      get<Unit[]>('/admin/units', { params: { status: 'available' } })
        .then((d) => setUnits(Array.isArray(d) ? d : []))
        .catch(() => setUnits([]))
      get<BusinessFormat[]>('/admin/business-formats')
        .then((d) => setBusinessFormats(Array.isArray(d) ? d : []))
        // 读失败不阻断成交录入（dealCategory 可选，下拉空即可）
        .catch(() => setBusinessFormats([]))
    }
  }, [kind])

  const close = () => {
    setKind(null)
    form.resetFields()
  }

  const doSubmit = async (fn: () => Promise<unknown>, okText: string) => {
    setSubmitting(true)
    try {
      await fn()
      message.success(okText)
      close()
      onDone()
    } catch (err: unknown) {
      message.error(errMsg(err, '操作失败'))
    } finally {
      setSubmitting(false)
    }
  }

  const markContacted = () => {
    Modal.confirm({
      title: '标记已联系',
      content: `确认将线索「${lead.name}（${lead.phone}）」状态推进为「已联系」？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () =>
        patch(`/admin/leads/${lead.id}/status`, { status: 'contacted' })
          .then(() => {
            message.success('已标记为已联系')
            onDone()
          })
          .catch((err: unknown) => message.error(errMsg(err, '状态更新失败'))),
    })
  }

  const submitFollowUp = async () => {
    const v = await form.validateFields()
    await doSubmit(
      () =>
        post(`/admin/leads/${lead.id}/followups`, {
          content: v.content,
          nextContactAt: v.nextContactAt ? v.nextContactAt.toISOString() : undefined,
        }),
      '跟进记录已保存',
    )
  }

  const submitTour = async () => {
    const v = await form.validateFields()
    await doSubmit(
      () =>
        post(`/admin/leads/${lead.id}/tour`, {
          tourAt: v.tourAt.toISOString(),
          tourUnit: v.tourUnit,
          tourFeedback: v.tourFeedback,
        }),
      '带看已登记',
    )
  }

  const submitDeal = async () => {
    const v = await form.validateFields()
    // deal_required_20260916：成交单元「可选可输」——选中字典项(unitId:number)关联字典并同步 unit 文本；
    // 手动输入(string)只落自由文本 unit，unitId 传 undefined。
    const unitField = v.unitId as UnitFieldValue
    const isDictPick = typeof unitField === 'number'
    const picked = isDictPick ? units.find((u) => u.id === unitField) : undefined
    const unitPayload = isDictPick
      ? { unitId: unitField as number, unit: picked ? `${picked.building} ${picked.floor}层 ${picked.unitNo}` : undefined }
      : { unitId: undefined, unit: typeof unitField === 'string' ? unitField.trim() : undefined }
    await doSubmit(
      () =>
        post(`/admin/leads/${lead.id}/deal`, {
          ...unitPayload,
          area: v.area,
          unitPrice: v.unitPrice,
          leaseMonths: v.leaseMonths,
          signedAt: v.signedAt.format('YYYY-MM-DD'),
          dealCategory: v.dealCategory,
        }),
      '成交已录入',
    )
  }

  const submitInvalid = async () => {
    const v = await form.validateFields()
    await doSubmit(() => post(`/admin/leads/${lead.id}/invalid`, { reason: v.reason }), '已转无效')
  }

  const submitTransfer = async () => {
    const v = await form.validateFields()
    await doSubmit(
      () => post(`/admin/leads/${lead.id}/assign`, { toUserId: Number(v.toUserId), reason: v.reason }),
      '转移成功，已写入留痕日志',
    )
  }

  // 权限层：SA/A 全量放行；operator 走 LEAD_EDIT_OWN
  // 归属层：仅持 LEAD_EDIT_OWN（不持 LEAD_EDIT）时，还须是本人名下线索
  const hasLeadEdit = hasPermission(user?.role, 'LEAD_EDIT')
  const hasLeadEditOwn = hasPermission(user?.role, 'LEAD_EDIT_OWN')
  const ownsLead = user?.adminId != null && lead.assigneeId === user.adminId
  const canOperate = hasLeadEdit || (hasLeadEditOwn && ownsLead)

  const canContact = canOperate && lead.status === 'pending'
  // tour_multi_v1：toured 后「带看登记」与「成交录入」并存（多次带看），其余状态门禁不变
  const canTour = canOperate && (lead.status === 'contacted' || lead.status === 'toured')
  const canDeal = canOperate && lead.status === 'toured'
  const canInvalid = canOperate && !['rewarded', 'invalid'].includes(lead.status)
  const canFollow = canOperate && !['rewarded', 'invalid'].includes(lead.status)

  return (
    <>
      {mobile ? (
        // leads-mobile-fix：手机端菜单化——同一组动作（同权限/状态过滤）渲染为 Dropdown menu.items
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              ...(canContact
                ? [{ key: 'contact', label: LEAD_STATUS_LABELS['contacted' as LeadStatus].label }]
                : []),
              ...(canFollow ? [{ key: 'followup', label: '跟进' }] : []),
              ...(canTour ? [{ key: 'tour', label: '带看登记' }] : []),
              ...(canDeal ? [{ key: 'deal', label: '成交录入' }] : []),
              ...(canInvalid ? [{ key: 'invalid', label: '转无效', danger: true }] : []),
              ...(canTransfer ? [{ key: 'transfer', label: '转移' }] : []),
            ],
            onClick: ({ key }) => {
              if (key === 'contact') markContacted()
              else if (key === 'followup') setKind('followup')
              else if (key === 'tour') setKind('tour')
              else if (key === 'deal') setKind('deal')
              else if (key === 'invalid') setKind('invalid')
              else if (key === 'transfer') setKind('transfer')
            },
          }}
        >
          <Button size="small" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      ) : (
      <Space size={4} wrap>
        {canContact && (
          <Button size="small" onClick={markContacted}>
            {LEAD_STATUS_LABELS['contacted' as LeadStatus].label}
          </Button>
        )}
        {canFollow && (
          <Button size="small" onClick={() => setKind('followup')}>
            跟进
          </Button>
        )}
        {canTour && (
          <Button size="small" onClick={() => setKind('tour')}>
            带看登记
          </Button>
        )}
        {canDeal && (
          <Button
            size="small"
            type="primary"
            style={{ background: '#27AE60', borderColor: '#27AE60' }}
            onClick={() => setKind('deal')}
          >
            成交录入
          </Button>
        )}
        {canInvalid && (
          <Button size="small" danger onClick={() => setKind('invalid')}>
            转无效
          </Button>
        )}
        {canTransfer && (
          <Button size="small" onClick={() => setKind('transfer')}>
            转移
          </Button>
        )}
      </Space>
      )}

      <Modal
        title="添加跟进记录"
        open={kind === 'followup'}
        onOk={submitFollowUp}
        onCancel={close}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="content" label="跟进内容" rules={[{ required: true, message: '跟进内容必填' }]}>
            <Input.TextArea rows={3} placeholder="例：电话沟通，客户对 300㎡ 整层有意向，下周复访" />
          </Form.Item>
          <Form.Item name="nextContactAt" label="下次联系时间（可选）">
            <DatePicker showTime style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={lead.status === 'toured' ? `带看登记（第 ${(lead.tourCount ?? 0) + 1} 次）` : '带看登记'}
        open={kind === 'tour'}
        onOk={submitTour}
        onCancel={close}
        okText={lead.status === 'toured' ? '登记带看' : '登记并推进为已带看'}
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ tourAt: dayjs() }}>
          <Form.Item name="tourAt" label="带看时间" rules={[{ required: true, message: '请选择带看时间' }]}>
            <DatePicker showTime style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Form.Item name="tourUnit" label="带看单元（可选）">
            <Input placeholder="例：T1-1201" />
          </Form.Item>
          <Form.Item name="tourFeedback" label="带看反馈（可选）">
            <Input.TextArea rows={3} placeholder="例：客户对采光满意，价格需再谈" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="成交录入"
        open={kind === 'deal'}
        onOk={submitDeal}
        onCancel={close}
        okText="确认成交"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ signedAt: dayjs() }}>
          {/* deal_required_20260916：成交单元改 AutoComplete——可从楼盘字典下拉选，也可手动输入任意楼盘文字 */}
          <Form.Item
            name="unitId"
            label="成交单元（可选择楼盘或手动输入）"
            rules={[{ required: true, message: '请选择成交单元或手动输入楼盘信息' }]}
          >
            <AutoComplete
              allowClear
              maxLength={32}
              filterOption={(inputValue, option) =>
                String(option?.label ?? '').toLowerCase().includes(String(inputValue).toLowerCase())
              }
              placeholder="选择已录入楼盘，或直接输入楼盘信息"
              options={units.map((u) => ({
                value: u.id,
                label: `${u.building} ${u.floor}层 ${u.unitNo}（${u.area}㎡）`,
              }))}
            />
          </Form.Item>
          <Form.Item name="area" label="面积（㎡）" rules={[{ required: true, message: '请填写面积' }]}>
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} placeholder="例：300" />
          </Form.Item>
          <Form.Item name="unitPrice" label="单价（元/㎡/月）" rules={[{ required: true, message: '请填写单价' }]}>
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} placeholder="例：120" />
          </Form.Item>
          <Form.Item name="leaseMonths" label="租期（月）" rules={[{ required: true, message: '请填写租期' }]}>
            <InputNumber min={1} max={120} style={{ width: '100%', minHeight: 44 }} placeholder="例：36" />
          </Form.Item>
          <Form.Item name="signedAt" label="签约日期" rules={[{ required: true, message: '请选择签约日期' }]}>
            <DatePicker style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          {/* deal_required_20260916：成交业态转必填（选项=业态表启用项） */}
          <Form.Item name="dealCategory" label="成交业态" rules={[{ required: true, message: '请选择成交业态' }]}>
            <Select
              placeholder="选择成交业态"
              options={businessFormats
                .filter((f) => f.status === 'published')
                .map((f) => ({ value: f.code, label: f.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="转为无效线索"
        open={kind === 'invalid'}
        onOk={submitInvalid}
        onCancel={close}
        okText="确认转无效"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="无效原因（必填，写入时间轴与审计）"
            rules={[{ required: true, message: '转无效必须填写原因' }]}
          >
            <Input.TextArea rows={3} placeholder="例：客户已签约其他楼宇 / 联系方式无效" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="转移线索跟进责任"
        open={kind === 'transfer'}
        onOk={submitTransfer}
        onCancel={close}
        okText="确认转移并留痕"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="toUserId"
            label="转移给（招商主理人账号 ID）"
            rules={[{ required: true, message: '请输入接收人账号 ID' }]}
          >
            <Input type="number" placeholder="输入招商主理人账号 ID" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="转移原因（必填，写入留痕日志）"
            rules={[{ required: true, message: '转移原因必填' }]}
          >
            <Input.TextArea rows={3} placeholder="例：王招商休假一周，转交赵招商继续跟进" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default LeadStatusActions
